import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import { createPro } from '@disco/state-management/actions/rights-data-actions'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'
import { getPros } from '@disco/state-management/selectors/contacts'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useCreateRightsEntity } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'
import { findDuplicateName } from '../../utils/validation'

/**
 * CreateProModal - Modal for creating a new PRO (Performance Rights Organization)
 *
 * This modal allows users to create a new PRO by providing a name
 * and optional ISNI identifier.
 *
 * When a PRO is created from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the new PRO.
 */
export function CreateProModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const pros = useSelector(getPros)
  const {
    searchText = '',
    splitId,
    splitsKey,
  } = (location.state as RightsDataModalLocationState) || {}

  const createEntity = useCreateRightsEntity({
    entityType: 'PRO',
    createThunk: createPro,
  })

  const [inputValue, setInputValue] = useState(searchText)

  const existingProNames = useMemo(() => pros.map(p => p.name), [pros])

  const duplicateProName = useMemo(
    () => findDuplicateName(inputValue, existingProNames),
    [inputValue, existingProNames],
  )

  const warningMessage = duplicateProName
    ? `${duplicateProName} already exists. Please double-check if this is a duplicate before saving.`
    : null

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      const createdProId = await createEntity(name, externalIdentifiers)

      // Auto-select the created PRO in the split that triggered the creation
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { proId: createdProId },
          }),
        )
      }
    },
    [dispatch, splitId, splitsKey, createEntity],
  )

  return (
    <BaseDialog
      heading="Create new PRO"
      nameFieldLabel="PRO name"
      onSave={handleSave}
      initialName={searchText}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showISNI
      open
    />
  )
}

export default CreateProModal
